import type { EventRecord, Transaction } from '@fleece/models';
import { NotFoundError } from '@fleece/utilities';
import { useState } from 'react';
import { CopyButton } from '@/components/copy-button';
import { Modal } from '@/components/modal';
import { Message, Skeleton } from '@/components/ui';
import { useOrderRecords } from '@/hooks/use-ledger';
import { easternDate, easternTime } from '@/lib/format';
import { jsonTokens, prettyJson, type JsonTokenKind } from '@/lib/json';
import { describeTrade, orderRecordLabel, quantity, quote, recordPayload, symbolLabel } from '@/lib/ledger';
import { cn } from '@/lib/utils';

/** The events the broker sent about the order behind one transaction, as they arrived. */
export function OrderEventsDialog({ transaction, onClose }: { transaction: Transaction | undefined; onClose: () => void }) {
  return (
    <Modal open={transaction?.referenceId !== undefined} title="Order events" onClose={onClose} className="w-[min(760px,calc(100vw-32px))]">
      {transaction?.referenceId !== undefined && <OrderEvents transaction={transaction} brokerOrderId={transaction.referenceId} />}
    </Modal>
  );
}

function OrderEvents({ transaction, brokerOrderId }: { transaction: Transaction; brokerOrderId: string }) {
  const records = useOrderRecords(brokerOrderId);
  const list = records.data?.records ?? [];
  // The latest by default: each event restates the whole order, so the last is the most complete.
  const [picked, setPicked] = useState<number | undefined>();
  const index = Math.min(picked ?? list.length - 1, list.length - 1);
  const record = list[index];
  const trade = describeTrade(transaction);

  return (
    <div className="flex min-h-0 flex-col gap-3 px-3 py-3">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-muted">Order</dt>
        <dd className="font-mono break-all select-all">{brokerOrderId}</dd>
        <dt className="text-muted">This fill</dt>
        <dd>
          <span className="capitalize">{trade.side}</span> · {trade.effect} · {quantity(transaction.size.abs())} {symbolLabel(transaction.symbol, transaction.assetClass)} at{' '}
          {quote(transaction.premium)} · {easternDate(transaction.timestamp)} {easternTime(transaction.timestamp)} ET
        </dd>
      </dl>

      {records.isPending ? (
        <div role="status" aria-label="Loading" className="flex flex-col gap-2">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : records.error !== null ? (
        <Message tone={records.error instanceof NotFoundError ? 'muted' : 'error'}>
          {records.error instanceof NotFoundError
            ? 'No broker order is recorded under this id, so there is no event to show. A fill booked straight into the ledger, rather than through order tracking, has none.'
            : records.error.message}
        </Message>
      ) : record === undefined ? (
        <Message>The order is recorded, but no event about it is.</Message>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {list.length > 1 ? (
              <EventTabs records={list} current={index} onPick={setPicked} />
            ) : (
              <span className="text-xs text-muted">One event · {orderRecordLabel(record, 0)}</span>
            )}
            <CopyButton value={prettyJson(recordPayload(record))} label="event JSON" />
          </div>
          <JsonView value={recordPayload(record)} />
        </>
      )}
    </div>
  );
}

/** One tab per event, oldest first, named by the status it reported. */
function EventTabs({ records, current, onPick }: { records: ReadonlyArray<EventRecord>; current: number; onPick: (index: number) => void }) {
  return (
    <div role="tablist" aria-label="Events, oldest first" className="flex flex-wrap gap-1">
      {records.map((record, index) => (
        <button
          key={index}
          type="button"
          role="tab"
          aria-selected={index === current}
          onClick={() => onPick(index)}
          className={cn(
            'h-7 rounded-md px-2 text-xs font-medium tabular transition-colors focus-visible:outline-2 focus-visible:outline-accent',
            index === current ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
          )}
        >
          {index + 1} · {orderRecordLabel(record, index)}
        </button>
      ))}
    </div>
  );
}

const TOKEN_CLASS: Readonly<Record<JsonTokenKind, string>> = {
  key: 'text-accent',
  string: 'text-positive',
  number: 'text-warning',
  literal: 'text-muted italic',
  punctuation: 'text-muted',
  space: '',
};

/** Indented, coloured JSON. The characters are exactly those the copy button copies. */
function JsonView({ value }: { value: unknown }) {
  return (
    <pre className="max-h-[55dvh] min-h-0 overflow-auto rounded-md border border-line bg-page p-2.5 font-mono text-[11px] leading-relaxed scrollbar-thin">
      {jsonTokens(prettyJson(value)).map((token, position) => (
        <span key={position} className={TOKEN_CLASS[token.kind]}>
          {token.text}
        </span>
      ))}
    </pre>
  );
}
